import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import TestimonialsCarousel from "@/components/TestimonialsCarousel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Brain,
  Target,
  Filter,
  Handshake,
  CheckCircle2,
  Coffee,
  Award,
  IdCard,
  BookOpen,
  Bot,
  Flame,
  CalendarDays,
  ChevronDown,
  Quote,
  User,
  Mail,
  Phone,
  MessageCircle,
  ShieldCheck,
  Lock,
  Unlock,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";

const WHATSAPP_NUMBER = "5516981960018";
const waLink = (msg: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const CTA_MSG =
  "Olá Junior! Quero garantir minha vaga no Vendas Pro 360° (21 e 22 de julho de 2026).";
const INFO_MSG =
  "Olá Junior! Quero mais informações sobre o Vendas Pro 360°.";

const LINK_5X = "https://mpago.li/2sm4yHa";
const LINK_PIX = "https://mpago.la/1Be2g8U";

type FieldState = "" | "ok" | "err";
const maskPhone = (raw: string) => {
  const v = raw.replace(/\D/g, "").substring(0, 11);
  let r = "";
  if (v.length > 0) r = "(" + v.substring(0, 2);
  if (v.length >= 3) r += ") " + v.substring(2, 7);
  if (v.length >= 8) r += "-" + v.substring(7, 11);
  return r;
};
const okEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
const okPhone = (p: string) => p.replace(/\D/g, "").length >= 10;

const testimonials = [
  {
    name: "Ciene Rodrigues",
    role: "CEO",
    company: "Zentai Benefícios",
    avatar: "CR",
    rating: 5,
    text: "Treinamento excelente com foco em IA aplicada às vendas. Ampliou nossa visão do processo comercial com insights práticos que elevaram a performance da equipe. Recomendo para quem quer desenvolver sua equipe e aumentar resultados.",
  },
  {
    name: "Silvete Araujo",
    role: "Diretora de Negócios",
    company: "Zentai Benefícios",
    avatar: "SA",
    rating: 5,
    text: "Uma experiência extremamente enriquecedora, que trouxe novas perspectivas sobre o uso da inteligência artificial em negociações e estratégias comerciais. Um treinamento de alto nível, que transforma a forma como conduzimos negócios e fortalecemos relacionamentos.",
  },
  {
    name: "Giovanna Amaro Gireli",
    role: "Coordenadora de Marketing",
    company: "Citróleo Group",
    avatar: "GG",
    rating: 5,
    text: "O treinamento com o Junior foi uma experiência extremamente valiosa. De forma dinâmica, ele trouxe insights poderosos para o time e também no nível pessoal, contribuindo diretamente para nossa evolução profissional e para o desenvolvimento da carreira.",
  },
  {
    name: "Marivaldo Souza",
    role: "CEO",
    company: "Triplos Tecnologia",
    avatar: "MS",
    rating: 5,
    text: "O treinamento proporcionou diversos insights e aprendizados valiosos. Compreendemos que todos têm um papel importante nas vendas. Tenho plena confiança de que nossa equipe vai evoluir e aproveitar cada oportunidade com mais estratégia e confiança.",
  },
];

const problemas = [
  {
    n: 1,
    color: "bg-accent",
    title: "Despreparo emocional",
    desc: "Travam sob pressão, perdem o controle na hora H e afastam o cliente sem perceber.",
  },
  {
    n: 2,
    color: "bg-primary",
    title: "Prospecção sem critério",
    desc: "Abordam qualquer pessoa, gastam energia com quem nunca vai comprar e culpam o mercado.",
  },
  {
    n: 3,
    color: "bg-[hsl(40_85%_45%)]",
    title: "Sem processo definido",
    desc: "Trabalham no improviso. Sem funil, sem follow-up, sem previsão de resultado.",
  },
  {
    n: 4,
    color: "bg-[hsl(210_60%_30%)]",
    title: "Objeções sem resposta",
    desc: "Na primeira resistência ficam em silêncio e perdem a venda para o concorrente.",
  },
];

const pilares = [
  { n: "01", t: "MÉTODO", d: "Do primeiro contato ao fechamento" },
  { n: "02", t: "TÉCNICA", d: "Ferramentas reais em dinâmicas práticas" },
  { n: "03", t: "MENTAL", d: "IE para vender sob pressão" },
];

const difs = [
  "Conteúdo direto ao ponto — sem enrolação",
  "Dinâmicas práticas em cada módulo",
  "Aplicação imediata no dia seguinte",
  "Turma reduzida com atenção individualizada",
];

const modulos = [
  {
    badge: "MÓD 1",
    title: "Inteligência Emocional em Vendas",
    sub: "Quem controla as emoções, controla a negociação.",
    color: "accent",
    items: [
      "Como a emoção sabota — ou potencializa — sua venda",
      "Técnicas para manter equilíbrio sob pressão e rejeição",
      "Gatilhos mentais: como ativar no momento certo",
      "Mindset de alta performance: o vendedor que não desiste",
      "Rapport — criar conexão genuína em segundos",
    ],
    tip: "Vendedor que se conhece vende mais.",
  },
  {
    badge: "MÓD 2",
    title: "Prospecção e Qualificação",
    sub: "O lead certo, na hora certa, com a abordagem certa.",
    color: "primary",
    items: [
      "Onde e como encontrar o cliente ideal para o seu produto",
      "Scripts de abordagem — WhatsApp e presencial",
      "Método BANT: qualifique antes de gastar energia",
      "Como organizar sua carteira e priorizar leads",
      "Prospecção ativa x passiva: quando usar cada uma",
    ],
    tip: "Prospeccionar bem é trabalhar menos para fechar mais.",
  },
  {
    badge: "MÓD 3",
    title: "Funil de Vendas e Follow-up",
    sub: "Quem não acompanha, perde para quem acompanha.",
    color: "gold",
    items: [
      "Como estruturar seu funil de acordo com seu ciclo de venda",
      "Cadência de follow-up: quando, como e quantas vezes",
      "Mensagens que reativam clientes sem parecer insistente",
      "Como usar o CRM a seu favor",
      "Gatilhos de urgência e escassez sem perder credibilidade",
    ],
    tip: "A venda mora no acompanhamento.",
  },
  {
    badge: "MÓD 4",
    title: "Negociação",
    sub: "Negociar não é ceder — é conduzir.",
    color: "blue",
    items: [
      "Os 4 perfis de compradores e como adaptar sua abordagem",
      "Técnicas de PNL aplicadas à negociação comercial",
      "Como apresentar o preço sem travar na objeção de valor",
      "Âncoragem, contraste e reciprocidade na prática",
      "Scripts para negociar condições sem perder margem",
    ],
    tip: "O vendedor que sabe negociar decide o resultado.",
  },
  {
    badge: "MÓD 5",
    title: "Objeções e Fechamento",
    sub: "Objeção não é negativa. É convite para fechar.",
    color: "accent",
    items: [
      "As 7 objeções mais comuns e como responder",
      "Técnica ACCE: Acolher, Confirmar, Contornar, Encaminhar",
      "Sinais de compra: saiba quando o cliente está pronto",
      "6 técnicas de fechamento para cada perfil",
      "Como criar senso de urgência real e ético",
    ],
    tip: "Fechar é consequência. Técnica é o caminho.",
  },
];

const colorMap: Record<string, { bg: string; text: string }> = {
  accent: { bg: "bg-accent", text: "text-accent-foreground" },
  primary: { bg: "bg-primary", text: "text-primary-foreground" },
  gold: { bg: "bg-[hsl(40_85%_45%)]", text: "text-white" },
  blue: { bg: "bg-[hsl(210_60%_30%)]", text: "text-white" },
};

const bonus = [
  { t: "Respostas prontas", d: "Scripts para as objeções mais comuns do seu mercado" },
  { t: "Scripts por perfil", d: "Abordagens adaptadas a cada perfil de cliente" },
  { t: "Simulação real", d: "Negocie com a IA e receba feedback imediato" },
  { t: "Disponível 24h", d: "Pratique onde e quando precisar" },
];

const incluso = [
  { icon: Brain, t: "5 Módulos Completos", d: "Inteligência emocional, prospecção, funil, negociação e fechamento com técnica ACCE." },
  { icon: Coffee, t: "Coffee Break", d: "Incluído nos 2 dias de treinamento." },
  { icon: Award, t: "Certificado", d: "Personalizado e reconhecido com seu nome." },
  { icon: IdCard, t: "Crachá de Participação", d: "Personalizado para os 2 dias de treinamento." },
  { icon: BookOpen, t: "Material Didático", d: "Apostila com conteúdo, dinâmicas e ferramentas práticas." },
  { icon: Bot, t: "Bônus: Objeção Zero AI", d: "IA exclusiva para praticar objeções, gerar scripts e simular negociações." },
];

function useCountdown(target: Date) {
  const [time, setTime] = useState(() => calc(target));
  useEffect(() => {
    const id = setInterval(() => setTime(calc(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return time;
}
function calc(target: Date) {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0 };
  return {
    d: Math.floor(diff / 864e5),
    h: Math.floor((diff % 864e5) / 36e5),
    m: Math.floor((diff % 36e5) / 6e4),
    s: Math.floor((diff % 6e4) / 1e3),
  };
}

const VendasPro360 = () => {
  const [open, setOpen] = useState<number | null>(null);
  const { d, h, m, s } = useCountdown(new Date("2026-07-21T08:00:00-03:00"));

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [stN, setStN] = useState<FieldState>("");
  const [stE, setStE] = useState<FieldState>("err");
  const [stT, setStT] = useState<FieldState>("");
  const [msgN, setMsgN] = useState("");
  const [msgE, setMsgE] = useState("⚠ E-mail inválido");
  const [msgT, setMsgT] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorTop, setErrorTop] = useState("");

  const validateAll = () => {
    let ok = true;
    if (nome.trim().length < 3) {
      setStN("err");
      setMsgN("⚠ Informe seu nome completo");
      ok = false;
    } else {
      setStN("ok");
      setMsgN("✓ Perfeito!");
    }
    if (!okEmail(email.trim())) {
      setStE("err");
      setMsgE("⚠ E-mail inválido");
      ok = false;
    } else {
      setStE("ok");
      setMsgE("✓ E-mail válido!");
    }
    if (!okPhone(tel)) {
      setStT("err");
      setMsgT("⚠ Número inválido");
      ok = false;
    } else {
      setStT("ok");
      setMsgT("✓ WhatsApp válido!");
    }
    return ok;
  };

  const enviarLead = async () => {
    setErrorTop("");
    if (!validateAll()) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-vendaspro360-lead", {
        body: { nome: nome.trim(), email: email.trim(), telefone: tel.trim(), origem: "VendasPro360" },
      });
      if (error) throw error;
      if (data && (data as any).ok === false) throw new Error((data as any).error || "Falha no envio");
      setSuccess(true);
    } catch (e: any) {
      setErrorTop(e?.message || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (s: FieldState) => (s === "err" ? "border-red-500 focus-visible:ring-red-500" : s === "ok" ? "border-green-500 focus-visible:ring-green-500" : "");
  const msgClass = (s: FieldState) => "text-xs mt-1 " + (s === "err" ? "text-red-500" : s === "ok" ? "text-green-500" : "text-transparent");

  const scrollToForm = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const form = document.getElementById('inscricao');
    if (form) {
       const offset = 80;
       const bodyRect = document.body.getBoundingClientRect().top;
       const elementRect = form.getBoundingClientRect().top;
       const elementPosition = elementRect - bodyRect;
       const offsetPosition = elementPosition - offset;
       window.scrollTo({
         top: offsetPosition,
         behavior: "smooth"
       });
    }
  };

  return (
    <div className="bg-background text-foreground min-h-screen">
      <Helmet>
        <title>Vendas Pro 360°: Do Contato à Fidelização | Bianchim Business</title>
        <meta
          name="description"
          content="Participe de 2 dias de imersão prática em vendas com Junior Bianchim. Aprenda do primeiro contato ao fechamento e fidelização. Turmas em 21 e 22 de julho."
        />
        <link rel="canonical" href="https://bianchimbusiness.com.br/vendaspro360_docontato_afidelizacao" />
        <meta property="og:title" content="Vendas Pro 360°: Do Contato à Fidelização | Bianchim Business" />
        <meta property="og:description" content="Participe de 2 dias de imersão prática em vendas com Junior Bianchim. Aprenda do primeiro contato ao fechamento e fidelização. Turmas em 21 e 22 de julho." />
        <meta property="og:image" content="https://bianchimbusiness.com.br/vendaspro360-og.jpg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://bianchimbusiness.com.br/vendaspro360_docontato_afidelizacao" />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="h-1.5 bg-primary" />
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="text-center md:text-left">
              <span className="inline-block bg-primary text-primary-foreground text-[10px] font-extrabold tracking-[0.25em] px-3 py-1.5 rounded mb-5">
                TREINAMENTO COMERCIAL
              </span>
              <h1 className="font-black leading-[0.92] tracking-tight text-5xl md:text-7xl text-foreground mb-3">
                VENDAS
                <span className="block text-primary">PRO 360°</span>
              </h1>
              <p className="italic text-muted-foreground text-lg md:text-xl mb-6">
                Do primeiro contato ao fechamento.<br />
                Do fechamento à fidelização.
              </p>
              <div className="inline-flex items-center gap-2 bg-[hsl(40_85%_45%)] text-white font-bold px-4 py-3 rounded-lg mb-4">
                <CalendarDays className="w-4 h-4" />
                21 e 22 de julho de 2026 | Turma Aberta
              </div>
              <div className="text-sm md:text-base text-muted-foreground mb-5 space-y-1 text-left inline-block">
                <p><strong>⏰ Horário:</strong> 19h às 22h30</p>
                <p><strong>📍 Local:</strong> Eccolo Coworking Criativo</p>
                <p className="pl-5">Rua Conde do Pinhal, 3237</p>
                <p className="pl-5">Vila Nery - São Carlos-SP</p>
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-md mx-auto md:mx-0">
                {["2 dias de imersão", "5 módulos completos", "Pessoa física e times", "+ Bônus IA exclusivo"].map((c) => (
                  <div key={c} className="bg-card border border-primary/25 text-primary text-xs font-semibold px-3 py-2.5 rounded-md text-center">
                    {c}
                  </div>
                ))}
              </div>
            </div>

            <Card className="bg-card border-primary/30 p-6 md:p-8 text-center">
              <p className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4">
                ⏰ Tempo até o início
              </p>
              <div className="grid grid-cols-4 gap-2 mb-6">
                {[{ n: d, l: "dias" }, { n: h, l: "horas" }, { n: m, l: "min" }, { n: s, l: "seg" }].map((x) => (
                  <div key={x.l} className="bg-background rounded-lg py-3">
                    <div className="text-2xl md:text-3xl font-black text-[hsl(40_85%_55%)] leading-none">
                      {String(x.n).padStart(2, "0")}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{x.l}</div>
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="w-full font-extrabold animate-pulse">
                <a href="#inscricao" onClick={scrollToForm}>
                  GARANTIR MINHA VAGA →
                </a>
              </Button>
              <p className="text-xs text-muted-foreground mt-3">a partir de 5x R$ 93,98</p>
            </Card>
          </div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="bg-muted/30 py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
            O PROBLEMA
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Por que vendedores perdem vendas?</h2>
          <div className="h-1 w-10 bg-primary rounded mb-5" />
          <p className="text-muted-foreground italic mb-8 max-w-3xl">
            Produto bom e preço justo não bastam. O que separa quem fecha de quem perde é método, preparo e consistência.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {problemas.map((p) => (
              <Card key={p.n} className="p-5 flex gap-4 items-start border-l-4 border-l-primary">
                <div className={`${p.color} w-8 h-8 rounded-full flex items-center justify-center text-sm font-extrabold text-white shrink-0`}>
                  {p.n}
                </div>
                <div>
                  <div className="font-bold text-primary mb-1">{p.title}</div>
                  <div className="text-sm text-muted-foreground">{p.desc}</div>
                </div>
              </Card>
            ))}
          </div>
          <div className="max-w-2xl mx-auto flex flex-col gap-6 mt-8">
            <div className="bg-primary text-primary-foreground font-extrabold italic text-center py-4 px-5 rounded-lg">
              A solução está em método, não em sorte.
            </div>
            <Button asChild className="w-full h-auto py-4 bg-purple-600 hover:bg-purple-700 text-white border-0 font-bold text-lg">
              <a href="#inscricao" onClick={scrollToForm}>
                Quero saber como resolver isso
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* AUTORIDADE */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-[hsl(40_85%_55%)] uppercase mb-2">
            QUEM VAI TE TREINAR
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Junior Bianchim</h2>
          <div className="h-1 w-10 bg-[hsl(40_85%_55%)] rounded mb-6" />

          <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
             <div>
                <img src="/junior-vendas.jpg" alt="Junior Bianchim" className="rounded-lg shadow-lg w-full max-w-sm mx-auto md:mx-0" />
             </div>
             <div>
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { n: "20+", l: "em vendas" },
                    { n: "100+", l: "empresas atendidas" },
                    { n: "1000+", l: "profissionais treinados" },
                  ].map((s) => (
                    <Card key={s.l} className="text-center py-6 px-3">
                      <div className="text-2xl md:text-4xl font-black text-[hsl(40_85%_55%)] leading-none">{s.n}</div>
                      <div className="text-[10px] md:text-xs text-muted-foreground mt-2">{s.l}</div>
                    </Card>
                  ))}
                </div>

                <ul className="space-y-3 mb-6">
                  {[
                    "Fundador da Bianchim Business",
                    "Professor Senac — Gestão e Negócios",
                    "Mais de 20 anos em grandes empresas e multinacionais",
                    "Especialista em vendas, negociação e desenvolvimento comercial",
                  ].map((c) => (
                    <li key={c} className="flex gap-3 items-start border-b border-border pb-3">
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                      <span className="text-sm md:text-base">{c}</span>
                    </li>
                  ))}
                </ul>

                <Card className="p-6 border-l-4 border-l-[hsl(40_85%_55%)]">
                  <Quote className="w-6 h-6 text-[hsl(40_85%_55%)] mb-2" />
                  <p className="italic text-muted-foreground">
                    "Você não vai encontrar um treinamento baseado apenas em teoria.<br/>O que vou compartilhar é o que aprendi em mais de 20 anos atuando na área comercial, negociando, liderando equipes e gerando resultados reais dentro de empresas.<br/><br/>É conteúdo prático, aplicável e pensado para quem quer vender mais."
                  </p>
                  <span className="block text-xs font-bold text-[hsl(40_85%_55%)] mt-3">
                    — Junior Bianchim | @junior.bianchim
                  </span>
                </Card>
             </div>
          </div>
        </div>
      </section>

      {/* O QUE É + DIFERENCIAIS (2 colunas no desktop) */}
      <section className="bg-muted/30 py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
                O TREINAMENTO
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-3">O que é o Vendas Pro 360°?</h2>
              <div className="h-1 w-10 bg-primary rounded mb-5" />
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Treinamento prático e intensivo que transforma vendedores em profissionais de alta performance — com método, técnica e mentalidade para vender mais, melhor e com consistência.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {pilares.map((p) => (
                  <Card key={p.n} className="text-center py-5 px-2 border-primary/25">
                    <div className="w-8 h-8 mx-auto bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-extrabold mb-2">
                      {p.n}
                    </div>
                    <div className="text-xs font-extrabold mb-1">{p.t}</div>
                    <div className="text-[11px] text-muted-foreground leading-tight">{p.d}</div>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-extrabold mb-4">Por que funciona</h3>
              <ul className="space-y-3">
                {difs.map((d) => (
                  <li key={d} className="flex gap-3 items-start">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="lg" className="mt-6 w-full md:w-auto">
                <a href="#inscricao" onClick={scrollToForm}>
                  Quero garantir minha vaga
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* MÓDULOS — 2 colunas no desktop */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
            CONTEÚDO PROGRAMÁTICO
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Os 5 Módulos</h2>
          <div className="h-1 w-10 bg-primary rounded mb-8" />

          <div className="grid md:grid-cols-2 gap-4">
            {modulos.map((mod, i) => {
              const c = colorMap[mod.color];
              const isOpen = open === i;
              return (
                <Card key={i} className="overflow-hidden">
                  <button
                    onClick={() => setOpen(isOpen ? null : i)}
                    className="w-full p-4 flex items-center gap-3 text-left"
                  >
                    <span className={`${c.bg} ${c.text} text-[10px] font-extrabold tracking-widest px-2 py-1 rounded shrink-0`}>
                      {mod.badge}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-primary">{mod.title}</div>
                      <div className="text-xs italic text-muted-foreground mt-0.5">{mod.sub}</div>
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 border-t border-border">
                      <ul className="space-y-2 my-3">
                        {mod.items.map((it) => (
                          <li key={it} className="flex gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                      <div className={`${c.bg} ${c.text} text-sm font-bold text-center py-2.5 rounded`}>
                        {mod.tip}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Button asChild size="lg">
              <a href="#inscricao" onClick={scrollToForm}>
                Quero garantir minha vaga
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* BÔNUS — 4 colunas desktop, 2 mobile */}
      <section className="bg-muted/30 py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <span className="inline-block bg-[hsl(40_85%_45%)] text-white text-[10px] font-extrabold tracking-[0.2em] px-3 py-1.5 rounded mb-4">
            🎁 BÔNUS EXCLUSIVO
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Objeção Zero AI</h2>
          <div className="h-1 w-10 bg-accent rounded mb-5" />
          <p className="italic text-muted-foreground mb-8 max-w-3xl">
            A ferramenta de IA exclusiva para participantes. Pratique, simule e melhore sua performance a qualquer hora.
          </p>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {bonus.map((b) => (
              <Card key={b.t} className="p-4 border-accent/30">
                <div className="flex items-center gap-2 font-bold mb-2">
                  <span className="w-5 h-5 bg-accent rounded-full flex items-center justify-center text-xs text-accent-foreground">
                    ✓
                  </span>
                  {b.t}
                </div>
                <p className="text-sm text-muted-foreground">{b.d}</p>
              </Card>
            ))}
          </div>

          <div className="flex justify-center">
            <a
              href="#inscricao"
              onClick={scrollToForm}
              className="inline-flex flex-wrap justify-center items-baseline gap-2 bg-accent hover:bg-accent/90 text-accent-foreground py-4 px-8 rounded-lg transition-colors cursor-pointer text-center"
            >
              <span className="font-bold text-base md:text-lg">Incluso para todos os participantes.</span>
              <span className="text-sm font-normal opacity-90">Garanta sua vaga agora.</span>
            </a>
          </div>
        </div>
      </section>

      {/* INCLUSO + INVESTIMENTO — 2 colunas */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
                O QUE ESTÁ INCLUSO
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Tudo que você recebe</h2>
              <div className="h-1 w-10 bg-primary rounded mb-6" />
              <div className="space-y-3">
                {incluso.map((it) => {
                  const Icon = it.icon;
                  return (
                    <Card key={it.t} className="p-4 flex gap-3 items-start border-l-4 border-l-primary">
                      <Icon className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                      <div>
                        <div className="font-bold text-primary">{it.t}</div>
                        <div className="text-sm text-muted-foreground">{it.d}</div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>

            <div>
              <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
                INVESTIMENTO
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Quanto custa?</h2>
              <div className="h-1 w-10 bg-primary rounded mb-6" />

              <Card className="border-2 border-primary p-6 md:p-8 text-center mb-5">
                <p className="text-[11px] font-extrabold tracking-widest text-muted-foreground uppercase mb-4">
                  Pessoa Física & Time Comercial (2–3 vendedores)
                </p>
                <div className="border-t border-border pt-4">
                  <p className="text-sm text-muted-foreground">Parcelado em</p>
                  <div className="text-5xl md:text-6xl font-black text-primary leading-none tracking-tight my-1">
                    R$ 93,98
                  </div>
                  <p className="text-xs text-muted-foreground italic">5x sem juros · por participante</p>
                </div>
                <div className="border-t border-border mt-4 pt-4">
                  <p className="text-sm text-muted-foreground">ou à vista por apenas</p>
                  <div className="text-4xl md:text-5xl font-black text-[hsl(40_85%_55%)] leading-none my-1">
                    R$ 399,41
                  </div>
                  <span className="inline-block bg-[hsl(40_85%_45%)] text-white text-xs font-bold px-3 py-1 rounded-full mt-2">
                    15% de desconto
                  </span>
                </div>
              </Card>

              <Card className="p-5 mb-5">
                <p className="text-sm font-bold text-primary mb-4">Tudo incluso no investimento:</p>
                <div className="flex flex-wrap gap-3">
                  {[
                    { text: "Coffee break", icon: Coffee, color: "text-orange-600" },
                    { text: "Certificado", icon: Award, color: "text-yellow-500" },
                    { text: "Crachá", icon: IdCard, color: "text-blue-500" },
                    { text: "Material didático", icon: BookOpen, color: "text-green-600" },
                    { text: "Objeção Zero AI", icon: Bot, color: "text-purple-500" },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <span key={item.text} className="flex items-center gap-2 bg-primary/10 border border-primary/30 text-sm md:text-base font-semibold px-4 py-2 rounded-full">
                        <Icon className={`w-5 h-5 ${item.color}`} />
                        {item.text}
                      </span>
                    );
                  })}
                </div>
              </Card>

              <Button asChild size="lg" className="w-full font-extrabold">
                <a href="#inscricao" onClick={scrollToForm}>
                  Quero meu lugar
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* DEPOIMENTOS reaproveitados */}
      <section className="bg-muted/30">
        <TestimonialsCarousel testimonials={testimonials} />
      </section>

      {/* CTA FINAL */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <Flame className="w-12 h-12 text-[hsl(40_85%_55%)] mx-auto mb-3" />
          <h2 className="text-4xl md:text-5xl font-black text-[hsl(40_85%_55%)] mb-2 leading-tight">
            VAGAS LIMITADAS.
          </h2>
          <p className="italic text-muted-foreground text-lg mb-8">
            Não deixe o seu concorrente chegar primeiro.
          </p>

          <ul className="text-left max-w-xl mx-auto space-y-2 mb-8">
            {[
              "5 módulos práticos com dinâmicas reais",
              "Coffee break, certificado, crachá e material didático",
              "Bônus exclusivo: Objeção Zero AI",
              "Turma reduzida — atenção individualizada",
              "Método com mais de 20 anos de resultado comprovado",
              "5x R$ 93,98 sem juros | ou R$ 399,41 à vista",
            ].map((l) => (
              <li key={l} className="flex gap-2 items-start">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <span>{l}</span>
              </li>
            ))}
          </ul>

          <Button asChild size="lg" className="text-base md:text-lg font-extrabold w-full max-w-md animate-pulse">
            <a href="#inscricao" onClick={scrollToForm}>
              GARANTIR MINHA VAGA AGORA →
            </a>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground">
            📅 21 e 22 de julho de 2026 | Turma Aberta<br />
            ⏰ 19h às 22h30<br />
            📍 <strong>Eccolo Coworking Criativo</strong> - Rua Conde do Pinhal, 3237 - Vila Nery - São Carlos-SP
          </p>
          <p className="mt-8 text-sm text-muted-foreground">
            <strong className="text-primary">@junior.bianchim</strong> | Bianchim Business — Educação Corporativa em Vendas
          </p>
        </div>
      </section>

      {/* INSCRIÇÃO FORMULARIO */}
      <section id="inscricao" className="py-16 md:py-20 bg-card border-t border-border relative">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-start">
             <div>
               <span className="inline-block text-[10px] font-extrabold tracking-[0.25em] text-primary uppercase mb-2">
                 GARANTA SUA VAGA
               </span>
               <h2 className="text-3xl md:text-4xl font-extrabold mb-3">Sua evolução começa aqui.</h2>
               <div className="h-1 w-10 bg-primary rounded mb-6" />
               <p className="text-muted-foreground mb-8">
                 Preencha seus dados ao lado para confirmar sua presença. As vagas são limitadas para preservar a profundidade da experiência.
               </p>

               <Card className="border-2 border-primary p-6 mb-6">
                 <p className="text-[11px] font-extrabold tracking-widest text-muted-foreground uppercase mb-4 text-center">
                   Investimento
                 </p>
                 <div className="border-t border-border pt-4 text-center">
                   <p className="text-sm text-muted-foreground">Parcelado em</p>
                   <div className="text-5xl md:text-6xl font-black text-primary leading-none tracking-tight my-2">
                     R$ 93,98
                   </div>
                   <p className="text-xs text-muted-foreground italic mb-4">5x sem juros</p>
                 </div>
                 <div className="border-t border-border pt-4 text-center">
                   <p className="text-sm text-muted-foreground">ou à vista por apenas</p>
                   <div className="text-4xl md:text-5xl font-black text-[hsl(40_85%_55%)] leading-none my-2">
                     R$ 399,41
                   </div>
                   <span className="inline-block bg-[hsl(40_85%_45%)] text-white text-xs font-bold px-3 py-1 rounded-full mt-2">
                     15% de desconto
                   </span>
                 </div>
               </Card>

               <div className="flex gap-4 items-center">
                  <ShieldCheck className="w-10 h-10 text-green-500 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    <strong>Pagamento 100% seguro.</strong> Processado pelo Mercado Pago com proteção total.
                  </p>
               </div>
             </div>

             <div className="bg-background rounded-xl p-6 md:p-8 border border-border shadow-2xl relative">
                <div className="flex items-center gap-3 mb-6">
                   <Lock className="w-5 h-5 text-muted-foreground" />
                   <h3 className="text-xl font-bold">Confirme seus dados</h3>
                </div>

                {!success ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-bold text-muted-foreground mb-1 block">Nome Completo</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                           className={`pl-10 ${inputClass(stN)}`}
                           placeholder="Seu nome completo"
                           value={nome}
                           onChange={(e) => setNome(e.target.value)}
                           onBlur={() => {
                             if (nome.trim().length < 3) {
                               setStN("err");
                               setMsgN("⚠ Informe seu nome completo");
                             } else {
                               setStN("ok");
                               setMsgN("✓ Perfeito!");
                             }
                           }}
                        />
                      </div>
                      {msgN && <p className={msgClass(stN)}>{msgN}</p>}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-muted-foreground mb-1 block">E-mail</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                           className={`pl-10 ${inputClass(stE)}`}
                           type="email"
                           placeholder="seu@email.com"
                           value={email}
                           onChange={(e) => setEmail(e.target.value)}
                           onBlur={() => {
                             if (okEmail(email.trim())) {
                               setStE("ok");
                               setMsgE("✓ E-mail válido!");
                             } else {
                               setStE("err");
                               setMsgE("⚠ E-mail inválido");
                             }
                           }}
                        />
                      </div>
                      {msgE && <p className={msgClass(stE)}>{msgE}</p>}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-muted-foreground mb-1 block">WhatsApp</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                           className={`pl-10 ${inputClass(stT)}`}
                           type="tel"
                           placeholder="(16) 99999-9999"
                           maxLength={15}
                           value={tel}
                           onChange={(e) => setTel(maskPhone(e.target.value))}
                           onBlur={() => {
                             if (okPhone(tel)) {
                               setStT("ok");
                               setMsgT("✓ WhatsApp válido!");
                             } else {
                               setStT("err");
                               setMsgT("⚠ Número inválido");
                             }
                           }}
                        />
                      </div>
                      {msgT && <p className={msgClass(stT)}>{msgT}</p>}
                    </div>

                    {errorTop && <p className="text-red-500 text-sm font-bold text-center mt-2">⚠ {errorTop}</p>}

                    <Button className="w-full font-bold h-12 mt-4 text-md bg-primary hover:bg-primary/90 text-primary-foreground" onClick={enviarLead} disabled={loading}>
                      {loading ? "ENVIANDO..." : "CONFIRMAR E LIBERAR PAGAMENTO"}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-black text-foreground mb-2">VAGA CONFIRMADA!</h3>
                    <p className="text-muted-foreground mb-8">
                      Enviamos um e-mail para <strong>{email}</strong> com os detalhes.
                      <br />Escolha sua forma de pagamento abaixo.
                    </p>

                    <div className="flex flex-col gap-3">
                      <Button asChild className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold">
                        <a href={LINK_5X} target="_blank" rel="noopener noreferrer">
                          <Unlock className="w-4 h-4 mr-2" />
                          PARCELAR EM 5X SEM JUROS
                        </a>
                      </Button>
                      <Button asChild className="w-full h-12 bg-green-600 hover:bg-green-700 text-white font-bold">
                        <a href={LINK_PIX} target="_blank" rel="noopener noreferrer">
                          <Unlock className="w-4 h-4 mr-2" />
                          PAGAR NO PIX · R$ 399,41
                        </a>
                      </Button>
                    </div>
                  </div>
                )}
             </div>
          </div>
        </div>
      </section>

      {/* STICKY mobile/desktop */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t-2 border-primary px-4 py-3 z-50 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-muted-foreground">a partir de</div>
          <div className="text-base md:text-lg font-black text-primary leading-none">5x R$ 93,98</div>
        </div>
        <Button asChild size="sm" className="font-extrabold">
          <a href="#inscricao" onClick={scrollToForm}>
            GARANTIR VAGA →
          </a>
        </Button>
      </div>
      <div className="h-20" />
      
      {/* WHATSAPP FLUTUANTE DE DÚVIDA */}
      <a 
        href={waLink("Olá Junior! Tenho uma dúvida sobre o Vendas Pro 360° e gostaria de falar com a equipe.")} 
        target="_blank" 
        rel="noreferrer" 
        className="fixed bottom-20 right-4 bg-green-500 hover:bg-green-600 text-white rounded-full p-3 shadow-2xl z-[60] transition-transform hover:scale-110 flex items-center justify-center group"
        aria-label="Falar no WhatsApp"
      >
        <span className="absolute right-full mr-3 whitespace-nowrap bg-white text-green-600 px-3 py-1.5 rounded-lg shadow-md text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Dúvidas?
        </span>
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

export default VendasPro360;
